'use client';

import { type SampleOrderType } from '@packages/gql/generated/shopping/graphql';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { useSampleOrderTracking } from '@packages/modules/src/Tracking/hooks/useSampleOrderTracking/useSampleOrderTracking';
import { LazySampleOrder } from '@packages/sample-order/src/utils/LazySampleOrder/LazySampleOrder.client';
import { SampleOrderErrorFallback } from '@packages/sample-order/src/utils/SampleOrderErrorFallback/SampleOrderErrorFallback.client';
import { SampleOrderSuspenseFallback } from '@packages/sample-order/src/utils/SampleOrderSuspenseFallback/SampleOrderSuspenseFallback';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { ErrorBoundary } from '@packages/shared/src/ui/ErrorBoundary/ErrorBoundary';
import { Suspense, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

export type SampleOrderLinkProps = {
  akl: string;
  sampleOrderType: SampleOrderType;
};

/**
 * This component is used to display a clickable call to action (CtA) link,
 * which opens a dialog that contains the `SampleOrder`.
 * */
export const SampleOrderLink = ({ akl, sampleOrderType }: SampleOrderLinkProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { trackSampleOrderClick, trackSampleOrderSeen } = useSampleOrderTracking();
  const { formatMessage } = useIntl();

  useEffect(() => {
    trackSampleOrderSeen(sampleOrderType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  return (
    <>
      <Button
        variant="link"
        onClick={() => {
          setIsDialogOpen(true);
          trackSampleOrderClick(sampleOrderType);
        }}
      >
        <span className="flex flex-row items-center gap-1 pr-2">
          <span className="icon-emp-fabricsamples size-3" />
          {formatMessage(
            sampleOrderType === 'WOOD'
              ? {
                  defaultMessage: 'Kostenlos Holzmuster bestellen',
                  id: 'sampleOrderCtA.link.woodPattern',
                }
              : {
                  defaultMessage: 'Kostenlos Stoffmuster bestellen',
                  id: 'sampleOrderCtA.link.fabricPattern',
                },
          )}
        </span>
      </Button>
      <Dialog isOpen={isDialogOpen} onClose={handleDialogClose}>
        <DialogHeader onClose={handleDialogClose}>
          {formatMessage({
            defaultMessage: 'Kostenlose Musterbestellung',
            id: 'sampleOrder.modalHeadline',
          })}
        </DialogHeader>
        <DialogContent className="p-0">
          <ErrorBoundary fallback={(reset) => <SampleOrderErrorFallback onCloseHandler={reset} />}>
            <Suspense fallback={<SampleOrderSuspenseFallback />}>
              <LazySampleOrder
                akl={akl}
                sampleOrderType={sampleOrderType}
                abortHandler={handleDialogClose}
              />
            </Suspense>
          </ErrorBoundary>
        </DialogContent>
      </Dialog>
    </>
  );
};
